import { COMPONENT_TYPE, NoData } from 'modules/experiments/components/ui';
import { dataTestId } from 'modules/experiments/constants';
import {
  useTakeoverExperimentContext,
  useVariantConfigurationContext,
} from 'modules/experiments/contexts';
import {
  getTakeoverCampaignId,
  getTakeoverExperimentUIState,
} from 'modules/experiments/ducks/selectors';
import { useExperimentsTranslations } from 'modules/experiments/hooks';
import { TemplatePreviewContextProvider } from 'modules/templateEditor/integrations';
import { TemplateTakeoverContextProvider } from 'modules/templateEditor/integrations/InStudioTemplateEditing/context';
import { useSelector } from 'react-redux';
import { ExperimentNotFound } from 'ui/illustrations/ExperimentNotFound';
import { Spinner } from 'ui/spinner';
import {
  Container,
  ContentSection,
  ExperimentConfigurationSection,
  StyledAnnouncement,
} from './Styled';
import { TemplateEditorTakeover } from './TemplateEditorTakeover';
import { Content, PageHeader, Settings, VariantConfiguration } from './sections';

type ExperimentTakeoverBodyProps = {
  handleOnClose: () => void;
};

export const ExperimentTakeoverBody = ({ handleOnClose }: ExperimentTakeoverBodyProps) => {
  const { t } = useExperimentsTranslations();
  const { errorState, loadingState } = useSelector(getTakeoverExperimentUIState);
  const campaignId = useSelector(getTakeoverCampaignId);
  const { selectedVariant } = useVariantConfigurationContext();
  const {
    isRunningExperiment,
    overlay: { isOverlayEnable },
  } = useTakeoverExperimentContext();

  if (errorState) {
    return (
      <NoData
        testId={dataTestId.noData}
        componentType={COMPONENT_TYPE.TAKEOVER}
        Icon={ExperimentNotFound}
        title={t('noData.takeover.title')}
        subTitle={t('noData.takeover.subTitle')}
        linkText={t('noData.takeover.link')}
        postText={t('noData.takeover.postText')}
        onClick={handleOnClose}
      />
    );
  }

  if (!selectedVariant?.id) return <Spinner />;

  return (
    <TemplatePreviewContextProvider
      campaignId={campaignId}
      selectedVariantTemplateId={selectedVariant?.id}
    >
      <TemplateTakeoverContextProvider>
        <PageHeader />
        {isRunningExperiment && (
          <StyledAnnouncement data-test={dataTestId.runningExperimentAnnouncement}>
            {t('announcement.runningExperiment')}
          </StyledAnnouncement>
        )}
        <Container isOverlayEnable={isOverlayEnable}>
          <ExperimentConfigurationSection>
            <VariantConfiguration loading={loadingState} />
            <Settings loading={loadingState} />
          </ExperimentConfigurationSection>
          <ContentSection>
            <Content loading={loadingState} />
          </ContentSection>
        </Container>
        <TemplateEditorTakeover />
      </TemplateTakeoverContextProvider>
    </TemplatePreviewContextProvider>
  );
};
